import { isIosCompanionApp } from "@/lib/native/runtime";
import type { CSSProperties, ReactNode } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

/**
 * Shared brand chrome for the seller auth surfaces (invite acceptance + sign-in),
 * recreating the Claude Design handoff "split screen + navy diamond" family using
 * the app's own tokens (`--color-hero-surface`, gold `--gold-*`, warm
 * `--canvas`) rather than the mock's raw hexes.

 * Anything painted ON the hero surface uses `--gold-*`, never `--accent-*`. The
 * hero is dark in BOTH themes, but the Trioty redesign turned `--accent-500`
 * into the near-black action color (gold moved to `--gold-*`) — so in light mode
 * every accent-colored mark here rendered #1f1f1b on #1a1a19 and vanished. The desktop navy panel and the
 * mobile navy header share the same decorative diamonds, logo, greeting accent
 * and BUILT FOR EGYPT footer so both screens read as one system.
 */

// The greeting accent follows the page's language. Arabic needs an
// Arabic-capable family because Space Grotesk (the Latin UI face) has no Arabic
// glyphs; English must NOT be forced into it, or the Latin greeting renders in a
// different face from the headline right below it.
const ARABIC_GREETING_FONT: CSSProperties = {
  fontFamily: "'Tajawal', 'IBM Plex Sans Arabic', system-ui, sans-serif",
};

export function greetingFont(locale: string): CSSProperties | undefined {
  return locale === "ar" ? ARABIC_GREETING_FONT : undefined;
}

// Low-opacity gold fills for the oversized diamonds that bleed off the panel
// corners. Literal rather than tokenized: these are alpha washes, not a color.
const DIAMOND_STRONG = "rgba(250, 185, 91, 0.07)";
const DIAMOND_SOFT = "rgba(250, 185, 91, 0.05)";

function AuthDiamonds({ mobile = false }: { mobile?: boolean }) {
  if (mobile) {
    return (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rotate-45"
        style={{ insetInlineEnd: -110, top: -140, width: 300, height: 300, background: DIAMOND_STRONG }}
      />
    );
  }
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rotate-45"
        style={{ insetInlineEnd: -140, top: -180, width: 460, height: 460, background: DIAMOND_STRONG }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rotate-45"
        style={{ insetInlineStart: -160, bottom: -200, width: 440, height: 440, background: DIAMOND_SOFT }}
      />
    </>
  );
}

/** 10px gold diamond used as a value-prop bullet on the hero panel. */
export function BrandDiamondBullet() {
  return (
    <span
      aria-hidden="true"
      className="h-2.5 w-2.5 shrink-0 rotate-45 bg-[color:var(--gold-500)]"
    />
  );
}

/** BUILT FOR EGYPT footer — dark tone lives in the navy panel, light tone at the
 *  bottom of the mobile form body. */
export function BuiltForEgypt({ tone }: { tone: "dark" | "light" }) {
  const t = useTranslations();
  if (tone === "dark") {
    return (
      <div className="relative flex flex-col gap-2.5 border-t border-[rgba(255,255,255,0.14)] pt-6">
        <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-[color:var(--gold-500)]">
          {t(keys.invite.heroFooterEyebrow)}
        </span>
        <span className="text-[14px] leading-relaxed text-[color:var(--color-hero-ink-subtle)]">
          {t(isIosCompanionApp() ? keys.iosCompanion.merchantTools : keys.invite.heroFooterBody)}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1 border-t border-[color:var(--border-light)] pt-3 text-center">
      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--accent-600)]">
        {t(keys.invite.heroFooterEyebrow)}
      </span>
      <span className="text-[12px] text-[color:var(--text-muted)]">
        {t(isIosCompanionApp() ? keys.iosCompanion.merchantTools : keys.invite.heroFooterBodyShort)}
      </span>
    </div>
  );
}

/** Desktop-only navy brand panel (560px, space-between: logo · message · footer). */
export function AuthBrandPanel({
  greeting,
  title,
  subtitle,
  children,
}: {
  greeting: string;
  title: string;
  subtitle: string;
  /** Optional extra content under the subtitle (e.g. value-prop list). */
  children?: ReactNode;
}) {
  const t = useTranslations();
  const locale = useLocale();
  return (
    <div className="relative hidden w-[560px] shrink-0 flex-col justify-between overflow-hidden bg-[color:var(--color-hero-surface)] px-14 pb-12 pt-14 text-[color:var(--color-hero-ink)] lg:flex">
      <AuthDiamonds />
      <img
        src="/logo-sidebar-dark.svg"
        alt={t(keys.common.appName)}
        className="relative self-start"
        style={{ height: 32, width: "auto" }}
      />
      <div className="relative flex flex-col gap-7">
        <span
          className="text-[20px] font-semibold text-[color:var(--gold-500)]"
          style={greetingFont(locale)}
        >
          {greeting}
        </span>
        <h1 className="m-0 text-[42px] font-bold leading-[1.15] tracking-[-0.02em] text-pretty">
          {title}
        </h1>
        <p className="m-0 max-w-[40ch] text-[17px] leading-relaxed text-[color:var(--color-hero-ink-subtle)]">
          {subtitle}
        </p>
        {children}
      </div>
      <BuiltForEgypt tone="dark" />
    </div>
  );
}

/** Mobile-only navy header (logo · greeting · title · tagline). */
export function AuthMobileHeader({
  greeting,
  title,
  tagline,
}: {
  greeting: string;
  title: string;
  tagline: string;
}) {
  const t = useTranslations();
  const locale = useLocale();
  return (
    <div className="relative flex flex-col gap-3.5 overflow-hidden bg-[color:var(--color-hero-surface)] px-6 pb-[30px] pt-7 text-[color:var(--color-hero-ink)] lg:hidden">
      <AuthDiamonds mobile />
      <img
        src="/logo-sidebar-dark.svg"
        alt={t(keys.common.appName)}
        className="relative self-start"
        style={{ height: 26, width: "auto" }}
      />
      <span
        className="relative text-[15px] font-semibold text-[color:var(--gold-500)]"
        style={greetingFont(locale)}
      >
        {greeting}
      </span>
      <h1 className="relative m-0 text-[26px] font-bold leading-tight tracking-[-0.02em]">
        {title}
      </h1>
      <span className="relative text-[13px] text-[color:var(--color-hero-ink-subtle)]">
        {tagline}
      </span>
    </div>
  );
}
